import PropTypes from "prop-types"
import { useEffect, useCallback, useRef, useMemo } from "react"
import { Helmet } from "react-helmet-async"
import { useDispatch, useSelector } from "react-redux"
import {
  useLocation,
  useNavigationType,
  useSearchParams,
  useParams,
} from "react-router-dom"

import { setExperiment } from "app-store-v2/app/actions"
import {
  clearDiamondTmpData,
  fetchStylecode,
  loadWizard,
  productFound,
  removeChoice,
  setWizardLoading,
  setExtraWizardParams,
  showMiniview,
  updateImages,
} from "areas/product/store/actions"
import { IS_GE_CHECKOUT, isTestMode } from "helpers/application"
import { getHreflangs } from "helpers/meta"

export default function fetchProduct(WrappedComponent) {
  const FetchProduct = ({ share, ...props }) => {
    const location = useLocation()
    const action = useNavigationType()
    const [searchParams] = useSearchParams()
    const { styleCode: paramStyleCode } = useParams()
    const is_ge_checkout = IS_GE_CHECKOUT()

    const currentHash = useRef(location.hash)

    const dispatch = useDispatch()
    const {
      adminMode,
      choices,
      experiment,
      formUrl,
      olid,
      pricing,
      productDescription,
      productReviews,
      productType,
      query_string,
      saleEndsIso,
      styleCode,
      wizard,
    } = useSelector((state) => ({
      adminMode: state.wizard.adminMode,
      choices: state.choices,
      experiment: state.experiment,
      formUrl: state.wizard.formUrl,
      olid: state.wizard.olid,
      pricing: state.wizardUI.currentPricing,
      productDescription: state.wizard.loading
        ? ""
        : // eslint-disable-next-line quotes
          state.wizard.description.product_description?.replace(/"/g, '\\"'),
      productReviews: state.wizard.productReviews,
      productType: state.wizard.description.product_type,
      query_string: state.wizard.query_string,
      saleEndsIso: state.wizard.price_and_promo.sale_ends_iso,
      styleCode: state.wizard.description.style_code,
      wizard: state.wizard,
    }))

    const loadProduct = () => {
      const styleCodeMatch = styleCode === paramStyleCode
      const ssr = action === "POP" && styleCodeMatch

      if (!ssr) {
        dispatch(clearDiamondTmpData())
        dispatch(setWizardLoading("full"))

        // On new product load, remove lid to allow add to cart
        dispatch(removeChoice("lid"))
        dispatch(setExtraWizardParams({ lid: "" }))
      }

      // load new product
      const search = searchParams.toString()
      if (
        styleCodeMatch &&
        (search === "" || search === query_string || formUrl)
      ) {
        dispatch(
          loadWizard({
            adminMode,
            olid,
            style_code: styleCode,
          })
        )
      } else {
        // Reset image slider to prevent previous loaded images from showing
        dispatch(
          updateImages({
            data: { all_views: [], images: {} },
          })
        )
        dispatch(showMiniview(false))
        dispatch(fetchStylecode(paramStyleCode, true))
      }
    }

    const resetProduct = useCallback(() => dispatch(productFound()), [])

    /* eslint-disable sort-keys */
    const reviewsSchema = useMemo(() => {
      if (!productReviews?.count) return {}

      return {
        aggregateRating: {
          "@type": "AggregateRating",
          ratingValue: productReviews.average_rating,
          reviewCount: productReviews.count,
          bestRating: 5,
        },
      }
    }, [productReviews, productTitle])
    /* eslint-enable sort-keys */

    // eslint-disable-next-line quotes
    let productTitle = wizard.title.replace(/"/g, '\\"')
    let productUrl = `https://${sl.config.site_url}${wizard.base_product_url}`
    let breadcrumbList = []

    if (wizard.description.breadcrumb) {
      breadcrumbList = wizard.description.breadcrumb.map((product, index) => ({
        "@type": "ListItem",
        item: {
          "@id": `https://${sl.config.site_url}${product.url}`,
          name: product.name,
        },
        position: index + 1,
      }))
      breadcrumbList.push({
        "@type": "ListItem",
        item: {
          "@id": productUrl,
          name: productTitle,
        },
        position: wizard.description.breadcrumb.length + 1,
      })
    }

    useEffect(() => {
      if (
        !isTestMode &&
        !wizard.loading &&
        Object.keys(wizard.data).length > 0
      ) {
        let itemSku = choices?.sku?.value || wizard.data.sku?.options[0]?.value
        let itemVariant = itemSku
          ? `${paramStyleCode}_${itemSku}`
          : paramStyleCode
        let itemCategory = wizard.productCategory

        let variant = wizard.data.sku?.options.find((op) => op.value == itemSku)
        let productBasePrice =
          variant?.display_price ||
          wizard?.data?.sku?.options[0]?.display_price ||
          0

        // Handle non_personalizable SKU Price
        // TODO: All wizard options should preload properly from backend
        if (
          productBasePrice == 0 &&
          wizard?.default_sku?.options?.length == 1
        ) {
          const sku_option = wizard.default_sku.options[0]
          productBasePrice = sku_option.sale_price || sku_option.price || 0
        }

        dataLayer.push({
          contentIds: [paramStyleCode],
          contentType: "product",
          currencyCode: sl.config.currency_code,
          ecommerce: {
            detail: {
              products: [
                {
                  name: paramStyleCode,
                },
              ],
            },
          },
          event: "viewed-product",
          ga4Items: [
            {
              currency: sl.config.currency_code,
              item_category: itemCategory,
              item_id: itemVariant,
              item_name: paramStyleCode,
              item_variant: itemSku,
              quantity: 1,
            },
          ],
          productBasePrice: productBasePrice,
          productCategory: itemCategory,
          productId: paramStyleCode,
          productImage:
            wizard?.description?.product_default_img ||
            wizard?.description?.product_img,
          productTitle: wizard?.seo_title,
          productUrl,
          viewProductUniqueEventId:
            window.cache_free_ssr_data.unique_event_ids.pdp,
        })

        let primaryTag = wizard?.primaryTag
        if (primaryTag) {
          dataLayer.push({
            event: "viewed-product-primary-tag",
            productPrimaryTag: primaryTag,
          })
        }
      }
    }, [wizard.loading])

    useEffect(() => {
      if (
        !isTestMode &&
        !wizard.loading &&
        Object.keys(wizard.data).length > 0 &&
        paramStyleCode &&
        choices?.sku?.icon_text
      ) {
        let itemStyleCode = choices?.style_code?.value
        let exp_key = `PPT_${itemStyleCode}`
        if (itemStyleCode && experiment[exp_key]) {
          dispatch(
            setExperiment({
              [exp_key]: experiment[exp_key],
            })
          )
        }
      }
    }, [wizard.loading, choices?.sku?.icon_text])

    useEffect(() => {
      if (action !== "REPLACE" || currentHash.current !== location.hash) {
        loadProduct()
        currentHash.current = location.hash
      }
      return () => {
        resetProduct()
      }
    }, [paramStyleCode])

    return (
      <>
        {!wizard.loading &&
          wizard.data.sku &&
          typeof wizard.data.sku === "object" && (
            <Helmet encodeSpecialCharacters={false}>
              {/* eslint-disable sort-keys */}
              <title>{wizard?.seo_title}</title>
              <meta content={wizard?.seo_title} name="og:title" />
              <meta content="og:product" property="og:type" />
              <meta content={productDescription} name="description" />
              <meta content={productDescription} property="og:description" />
              <meta
                content={share ? wizard?.shareUrl : productUrl}
                id="og_url"
                property="og:url"
              />
              <meta
                content={wizard?.og_price?.toFixed(2)}
                property="og:price:amount"
              />
              <meta
                content={sl.config.currency_code}
                property="og:price:currency"
              />
              <meta
                content={
                  wizard.header?.product_out_of_stock ? "oos" : "instock"
                }
                property="og:availability"
              />
              <meta
                content={wizard.description.style_code}
                property="style_code"
              />
              {wizard.base_product_url &&
                getHreflangs(wizard.base_product_url).map(
                  ({ href, hreflang }) => (
                    <link
                      href={href}
                      hrefLang={hreflang}
                      key={hreflang}
                      rel="alternate"
                    />
                  )
                )}
              <script type="application/ld+json">
                {JSON.stringify({
                  "@context": "https://schema.org/",
                  "@type": "Product",
                  name: productTitle,
                  url: productUrl,
                  image: wizard.description.product_img,
                  description: productDescription,
                  productID: wizard.description.style_code,
                  sku: wizard.description.style_code,
                  brand: {
                    "@type": "Brand",
                    name: sl.config.site_title,
                  },
                  ...(productType !== "ACCESSORY" && {
                    hasMerchantReturnPolicy: {
                      merchantReturnDays: is_ge_checkout ? "30" : "99",
                      merchantReturnLink: `https://${sl.config.site_url}/order/return`,
                    },
                  }),
                  offers: {
                    "@type": "Offer",
                    priceCurrency: sl.config.currency_code,
                    price:
                      pricing.price_to_display ||
                      wizard.data.sku.options[0].display_price,
                    ...(saleEndsIso && {
                      priceValidUntil: saleEndsIso,
                    }),
                    availability: `https://schema.org/${
                      wizard.header?.product_out_of_stock
                        ? "OutOfStock"
                        : "InStock"
                    }`,
                    itemCondition: "https://schema.org/NewCondition",
                  },
                  ...reviewsSchema,
                })}
              </script>

              {breadcrumbList.length > 0 && (
                <script type="application/ld+json">
                  {JSON.stringify({
                    "@context": "http://schema.org",
                    "@type": "BreadcrumbList",
                    itemListElement: breadcrumbList,
                  })}
                </script>
              )}
              {/* eslint-enable sort-keys */}
            </Helmet>
          )}
        <WrappedComponent {...props} />
      </>
    )
  }

  FetchProduct.propTypes = {
    share: PropTypes.bool,
  }

  return FetchProduct
}
